import { useEffect, useRef, useState } from "react"
import { IconAlertCircle, IconClockPause, IconRefresh } from "@tabler/icons-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { retryAfterSeconds } from "@/lib/calThrottled"
import { cn } from "@/lib/utils"

/** The public pages' copy (AB#514). No countdown in the sentence: the button, outside the alert, carries it. */
export const PUBLIC_CAL_THROTTLED_MESSAGE = "We can't check appointment times right now. Please try again in a moment."

/** The staff pages' fixed sentence: the only part of the notice in its live region. */
export const STAFF_CAL_THROTTLED_SENTENCE = "The scheduling service is busy."

/** The staff pages' countdown, shown beside the sentence and outside the live region. */
export function staffCalThrottledCountdown(seconds: number): string {
  return seconds > 0 ? `Try again in ${seconds} s.` : "You can try again now."
}

/** The staff pages' whole copy while the countdown runs, for callers that report it as one line. */
export function staffCalThrottledMessage(seconds: number): string {
  return `${STAFF_CAL_THROTTLED_SENTENCE} ${staffCalThrottledCountdown(seconds)}`
}

export interface CalThrottledNoticeProps {
  /** The throttled 503 (see `isCalThrottled`). A new one restarts the countdown from its wait. */
  error: unknown
  /** Asks again. Called by the Retry button, and at most once automatically when `autoRetry` is set. */
  onRetry: () => void
  /** "staff" for the calendars and booking dialogs; "public" for the booking website's copy. */
  variant?: "staff" | "public"
  /**
   * Retry by itself once, when the first countdown ends — for reads, which are safe to repeat. Never
   * more than once for the life of the notice: a second throttle after it waits for the button.
   * Left off for writes, which only the person who asked for them should send again.
   */
  autoRetry?: boolean
  /**
   * A retry is in flight: the button stays disabled until it answers. For reads that keep the notice
   * through the refetch; the save dialogs clear their notice before sending again, so they omit it.
   */
  busy?: boolean
  className?: string
}

/**
 * "The scheduling service is busy" (AB#514): what a page shows in place of its generic failure when
 * the API answers 503 `calThrottled`. Counts down the server's `retryAfterSeconds`, keeps Retry
 * disabled until it ends, and then lets it through.
 *
 * Counts down by decrementing once a second rather than against the clock: a timer the browser
 * throttles in a background tab only fires late, so the retry can be late but never early.
 */
export function CalThrottledNotice({
  error,
  onRetry,
  variant = "staff",
  autoRetry = false,
  busy = false,
  className,
}: CalThrottledNoticeProps) {
  const [remaining, setRemaining] = useState(() => retryAfterSeconds(error))
  const autoRetried = useRef(false)
  const onRetryRef = useRef(onRetry)
  useEffect(() => {
    onRetryRef.current = onRetry
  })

  // A fresh throttle starts a fresh wait. The automatic retry is not given back: it is once per notice.
  const lastError = useRef(error)
  useEffect(() => {
    if (lastError.current === error) return
    lastError.current = error
    setRemaining(retryAfterSeconds(error))
  }, [error])

  const counting = remaining > 0
  useEffect(() => {
    if (!counting) return
    const id = setInterval(() => setRemaining(current => Math.max(0, current - 1)), 1000)
    return () => clearInterval(id)
  }, [counting, error])

  useEffect(() => {
    if (counting || busy || !autoRetry || autoRetried.current) return
    autoRetried.current = true
    onRetryRef.current()
  }, [counting, busy, autoRetry])

  const disabled = counting || busy

  // Only the fixed sentence is a live region: the countdown and the button sit beside it, so a screen
  // reader announces the message once instead of on every tick.
  if (variant === "public") {
    return (
      <div className={cn("space-y-3", className)} data-testid="cal-throttled-notice">
        <Alert>
          <IconAlertCircle aria-hidden="true" />
          <AlertDescription>
            <p>{PUBLIC_CAL_THROTTLED_MESSAGE}</p>
          </AlertDescription>
        </Alert>
        <Button type="button" variant="outline" onClick={() => onRetry()} disabled={disabled}>
          <IconRefresh aria-hidden="true" />
          {counting ? `Try again in ${remaining} s` : "Try again"}
        </Button>
      </div>
    )
  }

  return (
    <div
      data-testid="cal-throttled-notice"
      className={cn("flex flex-wrap items-center gap-2 text-sm text-amber-700", className)}
    >
      <IconClockPause className="size-4 shrink-0" aria-hidden="true" />
      <span role="alert">{STAFF_CAL_THROTTLED_SENTENCE}</span>{" "}
      <span data-testid="cal-throttled-countdown">{staffCalThrottledCountdown(remaining)}</span>
      <Button type="button" size="xs" variant="outline" onClick={() => onRetry()} disabled={disabled}>
        <IconRefresh aria-hidden="true" />
        Retry
      </Button>
    </div>
  )
}
